import { openAreaHandler } from '../helpers/openAreaHandler.js'

const LBS_PER_KG = 2.205
export const IMPORT_CHUNK_SIZE = 200
export const PAGE_SIZE = 100

export const EXCEL_HEADERS = [
    'name',
    'model',
    'opening quantity',
    'opening width',
    'opening height',
    'horizontal modules',
    'vertical modules',
    'open area',
    'louver weight',
    'blank off panel',
    'back pan',
    'sill pan',
    'bird screen',
    'netEachPrice',
]

const COLUMN_DEFS = [
    {
        key: 'lineItemName',
        kind: 'string',
        required: true,
        aliases: ['name', 'nombre', 'lineitemname'],
    },
    {
        key: 'presentationModel',
        kind: 'string',
        aliases: ['model', 'modelo'],
    },
    {
        key: 'quantity',
        kind: 'number',
        required: true,
        aliases: ['openingquantity', 'openingqty'],
    },
    {
        key: 'width',
        kind: 'number',
        required: true,
        aliases: ['openingwidth', 'openingwidthin'],
    },
    {
        key: 'height',
        kind: 'number',
        required: true,
        aliases: ['openingheight', 'openingheightin'],
    },
    {
        key: 'horizontalModules',
        kind: 'number',
        required: true,
        aliases: ['horizontalmodules', 'hmodules'],
    },
    {
        key: 'verticalModules',
        kind: 'number',
        required: true,
        aliases: ['verticalmodules', 'vmodules'],
    },
    {
        key: 'excelOpenArea',
        kind: 'openArea',
        aliases: ['openarea', 'openarea%', 'openareapercent', 'areaabierta'],
    },
    {
        key: 'louverWeight',
        kind: 'number',
        required: true,
        minZero: true,
        aliases: ['louverweight'],
    },
    {
        key: 'blankOffPanel',
        kind: 'boolean',
        aliases: ['blankoffpanel', 'blankoff', 'blankpanel'],
    },
    {
        key: 'backPan',
        kind: 'boolean',
        aliases: ['backpan', 'backpanel'],
    },
    {
        key: 'sillPan',
        kind: 'boolean',
        aliases: ['sillpan', 'sillpanel'],
    },
    {
        key: 'birdScreen',
        kind: 'boolean',
        aliases: ['birdscreen'],
    },
    {
        key: 'fixedNetUnitPrice',
        kind: 'number',
        minZero: true,
        aliases: ['neteachprice', 'precioneto'],
    },
]

export function normalizeHeader(header) {
    if (header == null || header === '') return ''
    return header
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '')
}

export function parseYesNo(value) {
    if (value === true) return true
    if (value === false || value == null || value === '') return false
    const s = String(value)
        .trim()
        .toLowerCase()
    return s === 'yes' || s === 'si' || s === 'true' || s === '1'
}

export function getLouverWeightLbs(item) {
    if (item?.louverWeight != null && item.louverWeight !== '') {
        return Number(item.louverWeight)
    }
    if (item?.unitWeight != null && item.unitWeight !== '') {
        return Number(item.unitWeight) * LBS_PER_KG
    }
    return null
}

export function deriveLouverFields(item) {
    const quantity = Number(item.quantity)
    const width = Number(item.width)
    const height = Number(item.height)
    const h = Number(item.horizontalModules ?? item.hModules)
    const v = Number(item.verticalModules ?? item.vModules)
    return {
        louverQuantity: h * v * quantity,
        louverWidth: width / h,
        louverHeight: height / v,
    }
}

export function excelOpenAreaToRatio(value) {
    if (value == null || value === '') return null
    const n = Number(String(value).replace(/%/g, '').trim())
    if (Number.isNaN(n) || n < 0) return null
    return n > 1 ? n / 100 : n
}

export function ratioOpenAreaToExcel(ratio) {
    if (ratio == null || ratio === '') return ''
    const n = Number(ratio)
    if (Number.isNaN(n)) return ''
    return n * 100
}

function parseNumber(value, { minZero } = {}) {
    if (value == null || value === '') return null
    const n = Number(value)
    if (Number.isNaN(n)) return null
    if (minZero ? n < 0 : n <= 0) return null
    return n
}

function isEmptyRow(row) {
    return (
        !row ||
        row.every(
            cell =>
                cell === null ||
                cell === undefined ||
                (typeof cell === 'string' && cell.trim() === '')
        )
    )
}

export function parseCalculatorItemsSheet(jsonData) {
    if (!jsonData || jsonData.length < 2) {
        throw new Error(
            'The file must have at least a header row and one data row'
        )
    }
    const headers = (jsonData[0] || []).map(normalizeHeader)
    const columnIndex = {}
    for (const def of COLUMN_DEFS) {
        const idx = headers.findIndex(h => def.aliases.includes(h))
        if (idx >= 0) columnIndex[def.key] = idx
    }
    const missingHeaders = COLUMN_DEFS.filter(
        def => def.required && columnIndex[def.key] == null
    ).map(def => def.key)
    if (missingHeaders.length) {
        throw new Error(
            `Missing required column(s): ${missingHeaders.join(', ')}`
        )
    }

    const items = []
    jsonData.slice(1).forEach((row, rowOffset) => {
        if (isEmptyRow(row)) return
        const rowNumber = rowOffset + 2
        const item = {}
        for (const def of COLUMN_DEFS) {
            const idx = columnIndex[def.key]
            const raw = idx == null ? null : row[idx]
            if (def.kind === 'string') {
                const s = raw != null ? String(raw).trim() : ''
                if (def.required && !s) {
                    throw new Error(`Row ${rowNumber}: name is required`)
                }
                if (s) item[def.key] = s
            } else if (def.kind === 'boolean') {
                item[def.key] = parseYesNo(raw)
            } else if (def.kind === 'openArea') {
                const empty =
                    raw == null ||
                    raw === '' ||
                    (typeof raw === 'string' && raw.trim() === '')
                if (empty) continue
                const ratio = excelOpenAreaToRatio(raw)
                if (ratio == null) {
                    throw new Error(`Row ${rowNumber}: open area is invalid`)
                }
                item.excelOpenArea = ratio
            } else {
                const n = parseNumber(raw, { minZero: def.minZero })
                if (def.required && n == null) {
                    throw new Error(`Row ${rowNumber}: ${def.key} is required`)
                }
                if (n != null) item[def.key] = n
            }
        }
        Object.assign(item, deriveLouverFields(item))
        items.push(item)
    })

    if (!items.length) {
        throw new Error('No valid data found in the XLSX file')
    }
    return items
}

export function applyCalculatedOpenArea(items, model) {
    const prepared = (items || []).map(item => {
        const hModules = Number(item.horizontalModules || item.hModules) || 1
        const vModules = Number(item.verticalModules || item.vModules) || 1
        return {
            ...item,
            model,
            hModules,
            vModules,
            moduleWidth: (Number(item.width) * 0.0254) / hModules,
            moduleHeight: (Number(item.height) * 0.0254) / vModules,
        }
    })
    const needCalc = prepared.filter(
        item => item.excelOpenArea == null || Number.isNaN(Number(item.excelOpenArea))
    )
    if (needCalc.length) {
        const { calculateOpenArea } = openAreaHandler()
        calculateOpenArea(needCalc)
    }
    return prepared.map(item => {
        const override = Number(item.excelOpenArea)
        const ratio =
            item.excelOpenArea != null && !Number.isNaN(override)
                ? override
                : Number(item.openArea?.openArea)
        const derived = deriveLouverFields(item)
        const payload = {
            lineItemName: item.lineItemName,
            quantity: item.quantity,
            width: item.width,
            height: item.height,
            ...derived,
            horizontalModules: item.horizontalModules,
            verticalModules: item.verticalModules,
            openArea: { openArea: Number.isNaN(ratio) ? 0 : ratio },
            louverWeight: item.louverWeight,
            blankOffPanel: Boolean(item.blankOffPanel),
            backPan: Boolean(item.backPan),
            sillPan: Boolean(item.sillPan),
            birdScreen: Boolean(item.birdScreen),
        }
        if (item.presentationModel) {
            payload.presentationModel = item.presentationModel
        }
        if (item.fixedNetUnitPrice != null) {
            payload.fixedNetUnitPrice = item.fixedNetUnitPrice
        }
        if (item.netEachPrice != null) {
            payload.netEachPrice = item.netEachPrice
        }
        return payload
    })
}

export const RECALC_FIELDS = [
    'quantity',
    'width',
    'height',
    'horizontalModules',
    'verticalModules',
]

export function buildCalculatorItemPatch(item, field, value, model) {
    const patch = {}
    if (field === 'netEachPrice') {
        const n = Number(value)
        if (Number.isNaN(n) || n < 0) {
            throw new Error('Invalid price')
        }
        patch.fixedNetUnitPrice = n
    } else if (field === 'lineItemName') {
        const s = String(value == null ? '' : value).trim()
        if (!s) throw new Error('name is required')
        patch.lineItemName = s
    } else {
        const n = Number(value)
        if (Number.isNaN(n) || (field === 'louverWeight' ? n < 0 : n <= 0)) {
            throw new Error(`${field} is required`)
        }
        patch[field] = n
    }
    if (RECALC_FIELDS.includes(field)) {
        const merged = {
            ...item,
            ...patch,
            horizontalModules:
                patch.horizontalModules ??
                item.horizontalModules ??
                item.hModules,
            verticalModules:
                patch.verticalModules ?? item.verticalModules ?? item.vModules,
            louverWeight: patch.louverWeight ?? getLouverWeightLbs(item),
        }
        const [calculated] = applyCalculatedOpenArea([merged], model)
        patch.openArea = calculated.openArea
        Object.assign(patch, deriveLouverFields(merged))
    }
    return patch
}

export function formFromCalculatorItem(item) {
    return {
        lineItemName: item.lineItemName || '',
        quantity: item.quantity,
        width: item.width,
        height: item.height,
        horizontalModules: item.horizontalModules ?? item.hModules,
        verticalModules: item.verticalModules ?? item.vModules,
        louverWeight: getLouverWeightLbs(item),
        netEachPrice: item.fixedNetUnitPrice ?? item.netEachPrice ?? '',
    }
}

const ROW_PATCH_FIELDS = [
    'lineItemName',
    'quantity',
    'width',
    'height',
    'horizontalModules',
    'verticalModules',
    'louverWeight',
    'netEachPrice',
]

export function buildCalculatorItemRowPatch(item, form, model) {
    const patch = {}
    for (const field of ROW_PATCH_FIELDS) {
        if (
            field === 'netEachPrice' &&
            (form[field] === '' || form[field] == null)
        ) {
            continue
        }
        const fieldPatch = buildCalculatorItemPatch(
            item,
            field,
            form[field],
            model
        )
        delete fieldPatch.openArea
        Object.assign(patch, fieldPatch)
    }
    const merged = { ...item, ...patch }
    Object.assign(patch, deriveLouverFields(merged))
    const [calculated] = applyCalculatedOpenArea([merged], model)
    patch.openArea = calculated.openArea
    return patch
}

export function templateRowFromItem(item, quoteModel) {
    const weight = getLouverWeightLbs(item)
    const price =
        item.fixedNetUnitPrice != null
            ? item.fixedNetUnitPrice
            : item.netEachPrice
    const yesNo = v => (v ? 'YES' : 'NO')
    return [
        item.lineItemName || '',
        item.presentationModel || item.model || quoteModel || '',
        item.quantity ?? '',
        item.width ?? '',
        item.height ?? '',
        item.horizontalModules ?? item.hModules ?? '',
        item.verticalModules ?? item.vModules ?? '',
        ratioOpenAreaToExcel(item.openArea?.openArea),
        weight != null ? weight : '',
        yesNo(item.blankOffPanel),
        yesNo(item.backPan),
        yesNo(item.sillPan),
        yesNo(item.birdScreen),
        price != null ? price : '',
    ]
}
